import { useApp, useBehavior, useInput, useTriggerAction } from '@libs/hooks';
import clsx from 'clsx';
import React, { useRef, ChangeEvent, KeyboardEvent, ClipboardEvent, Activity, useState } from 'react';
import { TInputData, TWidgetProps } from '@libs/types';
import { TOTPOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './otpInput.module.css';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';

export const OtpInput = ({ widget }: TWidgetProps<TInputData, TOTPOption>) => {
    const { margin, onFocus, codeLength = 6, allowAllSymbols = false, dividerPosition = 0, isHideSymbols = false } = widget.options ?? ({} as TOTPOption);
    const data = widget.data ?? {};
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const { value, isError, errorMessage, handleChange, handleFocus, handleBlur } = useInput(
        {
            ...widget,
            data: { ...widget.data, submitLength: codeLength },
        },
        { isHidden, isDisabled, triggerActionsByType, setModal },
    );
    const [focusedInput, setFocusedInput] = useState<number | null>(null);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const otpArray = value.split('').slice(0, codeLength);
    const firstEmptyIndex = otpArray.length;
    while (otpArray.length < codeLength) {
        otpArray.push('');
    }

    const setFocus = (inputSelectedIndex: number) => {
        setTimeout(() => inputRefs.current[inputSelectedIndex]?.focus(), 0);
    };

    const changeValue = (e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement> | ClipboardEvent<HTMLInputElement>, newValue: string) => {
        handleChange?.({ ...e, target: { ...e.target, value: newValue } } as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const handleChangeOtp = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const val = e.target.value;
        const char = val.slice(-1);

        const newOtp = [...otpArray];
        newOtp[index] = char;
        const newValue = newOtp.join('');

        changeValue(e, newValue);

        if (char !== '' && index < codeLength - 1) {
            setFocus(index + 1);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const newOtp = [...otpArray];

            if (otpArray[index] !== '') {
                newOtp[index] = '';
                changeValue(e, newOtp.join(''));
            } else if (index > 0) {
                newOtp[index - 1] = '';
                changeValue(e, newOtp.join(''));
                setFocus(index - 1);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            setFocus(index - 1);
        } else if (e.key === 'ArrowRight' && index < codeLength - 1) {
            e.preventDefault();

            if (firstEmptyIndex >= index + 1) {
                setFocus(index + 1);
            }
        }
    };

    const handleFocusOtp = (e: React.FocusEvent<HTMLInputElement>, index: number) => {
        setFocusedInput(index);
        if (firstEmptyIndex !== -1 && firstEmptyIndex < index) {
            setFocus(firstEmptyIndex);
        } else {
            e.target.select();
        }
        handleFocus?.(e);
    };

    const handleBlurOtp = (e: React.FocusEvent<HTMLInputElement>, _index: number) => {
        setFocusedInput(null);
        handleBlur?.({ ...e, target: { ...e.target, value: value } } as React.FocusEvent<HTMLInputElement>);
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, codeLength);
        changeValue(e, pastedData);

        const nextFocus = Math.min(pastedData.length, codeLength - 1);
        setFocus(nextFocus);
    };

    const isAddSeparator = (index: number) => {
        if (dividerPosition > 0 && index + 1 !== codeLength) {
            return (index + 1) % dividerPosition === 0;
        }
        return false;
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div data-test={widget.uid} className={styles.container} style={getInlineStyles({ margin })}>
                <div className={styles.inputGroup}>
                    {otpArray.map((char, index) => (
                        <React.Fragment key={index}>
                            <InputWrapper
                                dataTest={`${widget.uid}_input_${index}`}
                                widget={widget}
                                isDisabled={isDisabled}
                                isHidden={isHidden}
                                isFocused={focusedInput === index}
                                isError={isError}
                                options={{
                                    padding: '8px',
                                    width: '40px',
                                    height: '56px',
                                }}
                            >
                                <input
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    className={styles.typografy}
                                    type={isHideSymbols ? 'password' : 'text'}
                                    inputMode={allowAllSymbols ? 'text' : 'numeric'}
                                    value={char}
                                    autoFocus={onFocus && index === codeLength - 1}
                                    onChange={(e) => handleChangeOtp(e, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onFocus={(e) => handleFocusOtp(e, index)}
                                    onBlur={(e) => handleBlurOtp(e, index)}
                                    onPaste={handlePaste}
                                    tabIndex={index === 0 && !isDisabled && !isHidden ? data.tabIndex : -1}
                                    disabled={isDisabled}
                                />
                            </InputWrapper>
                            {isAddSeparator(index) && <span className={clsx(styles.separator, styles.typografy)}>-</span>}
                        </React.Fragment>
                    ))}
                </div>
                {isError && <span className={styles.errorMessage}>{errorMessage}</span>}
            </div>
        </Activity>
    );
};
